import { useState, useCallback } from 'react';
import { Image as ImageIcon, Download, Upload, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const FAVICON_SIZES = [16, 32, 48, 64, 96, 128, 192, 256, 512];

interface GeneratedFavicon {
    size: number;
    dataUrl: string;
}

const FaviconGenerator = () => {
    const [originalImage, setOriginalImage] = useState<string | null>(null);
    const [favicons, setFavicons] = useState<GeneratedFavicon[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectedSizes, setSelectedSizes] = useState<number[]>([16, 32, 48, 192, 512]);

    const generateFavicon = useCallback((imageUrl: string, size: number): Promise<string> => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = size;
                canvas.height = size;

                // Draw with smooth scaling
                ctx!.imageSmoothingEnabled = true;
                ctx!.imageSmoothingQuality = 'high';
                ctx!.drawImage(img, 0, 0, size, size);

                resolve(canvas.toDataURL('image/png'));
            };

            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = imageUrl;
        });
    }, []);

    const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }

        const imageUrl = URL.createObjectURL(file);
        setOriginalImage(imageUrl);
        setIsGenerating(true);

        try {
            const generated: GeneratedFavicon[] = [];

            for (const size of selectedSizes) {
                const dataUrl = await generateFavicon(imageUrl, size);
                generated.push({ size, dataUrl });
            }

            setFavicons(generated);
            toast.success('Favicons generated!');
        } catch (error) {
            toast.error('Failed to generate favicons');
        } finally {
            setIsGenerating(false);
        }
    }, [selectedSizes, generateFavicon]);

    const regenerate = useCallback(async () => {
        if (!originalImage) return;

        setIsGenerating(true);

        try {
            const generated: GeneratedFavicon[] = [];

            for (const size of selectedSizes) {
                const dataUrl = await generateFavicon(originalImage, size);
                generated.push({ size, dataUrl });
            }

            setFavicons(generated);
            toast.success('Favicons regenerated!');
        } catch (error) {
            toast.error('Failed to regenerate');
        } finally {
            setIsGenerating(false);
        }
    }, [originalImage, selectedSizes, generateFavicon]);

    const toggleSize = (size: number) => {
        setSelectedSizes(prev =>
            prev.includes(size)
                ? prev.filter(s => s !== size)
                : [...prev, size].sort((a, b) => a - b)
        );
    };

    const downloadFavicon = (favicon: GeneratedFavicon) => {
        const a = document.createElement('a');
        a.href = favicon.dataUrl;
        a.download = `favicon-${favicon.size}x${favicon.size}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(`Downloaded ${favicon.size}x${favicon.size}`);
    };

    const downloadAll = () => {
        favicons.forEach((favicon, i) => {
            setTimeout(() => downloadFavicon(favicon), i * 200);
        });
    };

    const downloadIco = async () => {
        // Download 16x16 as favicon.ico (browsers accept PNG)
        const favicon16 = favicons.find(f => f.size === 16);
        if (favicon16) {
            const a = document.createElement('a');
            a.href = favicon16.dataUrl;
            a.download = 'favicon.ico';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success('Downloaded favicon.ico');
        } else {
            toast.error('Please include 16x16 size');
        }
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-primary/10">
                        <ImageIcon className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Favicon Generator</h1>
                        <p className="text-muted-foreground">Create favicons from any image</p>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Upload & Settings */}
                <Card>
                    <CardHeader>
                        <CardTitle>Upload Image</CardTitle>
                        <CardDescription>Upload a square image for best results</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* Upload Area */}
                        <label className="block mb-6">
                            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors">
                                {originalImage ? (
                                    <div className="flex flex-col items-center">
                                        <img
                                            src={originalImage}
                                            alt="Original"
                                            className="w-24 h-24 object-cover rounded-lg mb-3"
                                        />
                                        <p className="text-sm text-muted-foreground">Click to change image</p>
                                    </div>
                                ) : (
                                    <>
                                        <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                                        <p className="font-medium">Drop image here or click to upload</p>
                                        <p className="text-sm text-muted-foreground mt-1">PNG, JPG, SVG recommended</p>
                                    </>
                                )}
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                            />
                        </label>

                        {/* Size Selection */}
                        <div className="mb-6">
                            <p className="text-sm font-medium mb-3">Select Sizes (px)</p>
                            <div className="flex flex-wrap gap-2">
                                {FAVICON_SIZES.map(size => (
                                    <button
                                        key={size}
                                        onClick={() => toggleSize(size)}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedSizes.includes(size)
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-muted hover:bg-muted/80'
                                            }`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {originalImage && (
                            <Button
                                onClick={regenerate}
                                className="w-full"
                                disabled={isGenerating || selectedSizes.length === 0}
                            >
                                {isGenerating ? 'Generating...' : 'Regenerate Favicons'}
                            </Button>
                        )}
                    </CardContent>
                </Card>

                {/* Preview & Download */}
                <Card>
                    <CardHeader>
                        <div className="flex justify-between items-start">
                            <div>
                                <CardTitle>Generated Favicons</CardTitle>
                                <CardDescription>{favicons.length} sizes generated</CardDescription>
                            </div>
                            {favicons.length > 0 && (
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={downloadIco} variant="outline">
                                        .ico
                                    </Button>
                                    <Button size="sm" onClick={downloadAll}>
                                        <Download className="h-4 w-4 mr-1" />
                                        All
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {favicons.length > 0 ? (
                            <div className="grid grid-cols-3 gap-4">
                                {favicons.map((favicon) => (
                                    <div
                                        key={favicon.size}
                                        onClick={() => downloadFavicon(favicon)}
                                        className="group cursor-pointer"
                                    >
                                        <div className="aspect-square bg-muted rounded-lg flex items-center justify-center p-2 group-hover:bg-primary/10 transition-colors relative">
                                            <img
                                                src={favicon.dataUrl}
                                                alt={`${favicon.size}x${favicon.size}`}
                                                className="max-w-full max-h-full"
                                                style={{
                                                    imageRendering: favicon.size < 64 ? 'pixelated' : 'auto',
                                                    width: Math.min(favicon.size, 80),
                                                    height: Math.min(favicon.size, 80)
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                                <Download className="h-6 w-6 text-white" />
                                            </div>
                                        </div>
                                        <p className="text-xs text-center mt-2 text-muted-foreground">
                                            {favicon.size}x{favicon.size}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="h-64 border-2 border-dashed border-border rounded-xl flex items-center justify-center">
                                <div className="text-center text-muted-foreground">
                                    <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Upload an image to generate favicons</p>
                                </div>
                            </div>
                        )}

                        {/* Usage Instructions */}
                        {favicons.length > 0 && (
                            <div className="mt-6 p-4 rounded-lg bg-muted">
                                <p className="text-sm font-medium mb-2">How to use:</p>
                                <code className="text-xs text-muted-foreground block">
                                    {'<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">'}
                                </code>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default FaviconGenerator;
